import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { memoryStatsService, type MemoryStats } from '@/services/memory-stats-service';
import {
  Card, CardHeader, CardTitle, CardContent, Empty, Spinner, Badge,
  DatePicker, Field, FieldLabel, Button,
} from 'prosci-ui';
import { parseDateLocal } from '@/lib/date-utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

// Prosci Damson, mirroring --primary-500/400/100 in
// packages/prosci-ui/src/styles/globals.css. Recharts writes these as SVG
// presentation attributes, which cannot resolve var(), so the scale is
// restated here rather than referenced.
const DAMSON = '#350944';
const DAMSON_400 = '#6E3879';
const DAMSON_100 = '#E4D3E9';

const DEFAULT_WINDOW_DAYS = 30;

function isoDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function toIso(date: Date | undefined) {
  return date ? date.toISOString().slice(0, 10) : undefined;
}

function formatDay(iso: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function Stat({ label, value, subtext, tone }: {
  label: string;
  value: string | number;
  subtext: string;
  tone?: 'muted';
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className={`text-2xl font-semibold ${tone === 'muted' ? 'text-muted-foreground' : 'text-primary'}`}>
          {value}
        </div>
        <div className="text-sm font-medium mt-1">{label}</div>
        <div className="text-xs text-muted-foreground">{subtext}</div>
      </CardContent>
    </Card>
  );
}

export function MemorySection() {
  const [from, setFrom] = useState<string>(isoDaysAgo(DEFAULT_WINDOW_DAYS));
  const [to, setTo] = useState<string>(toIso(new Date()) as string);

  const { data, isLoading } = useQuery({
    queryKey: ['kaiya-memory-stats', from, to],
    queryFn: () => memoryStatsService.getStats(from, to),
    refetchInterval: 60000,
  });

  const resetWindow = () => {
    setFrom(isoDaysAgo(DEFAULT_WINDOW_DAYS));
    setTo(toIso(new Date()) as string);
  };

  // Same white filter surface the reports pages use, so the row reads as part
  // of the dashboard rather than floating on the page background. Fields get a
  // real column each — a formatted date overflows a narrow fixed width.
  const controls = (
    <Card className="mb-6">
      <CardContent className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,15rem)_minmax(0,15rem)_auto] gap-4 items-end">
          <Field>
            <FieldLabel htmlFor="memory-from">From</FieldLabel>
            <DatePicker
              id="memory-from"
              date={from ? parseDateLocal(from) : undefined}
              onDateChange={(d: Date | undefined) => setFrom(toIso(d) ?? isoDaysAgo(DEFAULT_WINDOW_DAYS))}
              placeholder="Select date"
              captionLayout="dropdown"
              fromYear={2020}
              toYear={new Date().getFullYear()}
              className="h-9 w-full justify-start normal-case bg-white hover:bg-white text-sm px-3"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="memory-to">To</FieldLabel>
            <DatePicker
              id="memory-to"
              date={to ? parseDateLocal(to) : undefined}
              onDateChange={(d: Date | undefined) => setTo(toIso(d) ?? (toIso(new Date()) as string))}
              placeholder="Select date"
              captionLayout="dropdown"
              fromYear={2020}
              toYear={new Date().getFullYear()}
              className="h-9 w-full justify-start normal-case bg-white hover:bg-white text-sm px-3"
            />
          </Field>
          <Button variant="outline" className="h-9 justify-self-start" onClick={resetWindow}>
            Last {DEFAULT_WINDOW_DAYS} days
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) return <>{controls}<Empty><Spinner /></Empty></>;
  const s = data as MemoryStats | undefined;
  if (!s) return <>{controls}</>;

  const { adoption: a, behaviour: b, retention: r } = s;

  const adoptionSplit = [
    { name: 'Memory on', value: a.users_with_memory_on },
    { name: 'Eligible, not on', value: Math.max(a.eligible_users - a.users_with_memory_on, 0) },
  ];

  const funnel = [
    { stage: 'Opted in', people: b.opted_in },
    { stage: 'Edited profile', people: b.profile_edited },
    { stage: 'Opened pill', people: b.pill_opened },
    { stage: 'Opened settings', people: b.manage_opened },
    { stage: 'Deleted all', people: b.all_deleted },
    { stage: 'Opted out', people: b.opted_out },
    { stage: 'Declined intro', people: b.intro_dismissed },
  ];

  const noBehaviourYet = funnel.every((f) => f.people === 0) && b.memory_used_conversations === 0;
  // The caveat only earns its place when the chosen window reaches back past
  // the day tracking started; otherwise every number in it is complete.
  const windowPredatesTracking = !s.tracking_since || new Date(s.tracking_since) > new Date(s.window_from);
  const trackingNote = windowPredatesTracking
    ? s.tracking_since
      ? `Only counts activity since tracking began on ${formatDay(s.tracking_since)}.`
      : 'Nothing tracked yet — these fill in once the instrumentation has been live for a while.'
    : null;

  return (
    <>
      {controls}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Stat label="Orgs with memory on" value={a.orgs_with_memory_on} subtext="not disabled" />
        <Stat label="Users with memory on" value={a.users_with_memory_on} subtext="individually opted in" />
        <Stat label="Adoption" value={`${a.adoption_rate}%`} subtext={`of ${a.eligible_users} eligible users`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader><CardTitle>Adoption among eligible users</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={adoptionSplit} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} label>
                    {adoptionSplit.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? DAMSON : DAMSON_100} />
                    ))}
                  </Pie>
                  <Legend />
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              How long people keep it on
              <Badge variant="outline" className="text-[10px]">measured</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <div>
              <div className="text-2xl font-semibold text-primary">{r.still_on_median_days ?? '—'}</div>
              <div className="text-sm">median days on, people still using it</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-primary">{r.median_days_held ?? '—'}</div>
              <div className="text-sm">median days before turning it off</div>
              <div className="text-xs text-muted-foreground">{r.churned} turned it off in this window</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>What people do with memory</CardTitle>
          <p className="text-xs text-muted-foreground">
            Distinct people, {formatDay(s.window_from)} – {formatDay(s.window_to)}.
            {trackingNote ? ` ${trackingNote}` : ''}
          </p>
        </CardHeader>
        <CardContent>
          {noBehaviourYet ? (
            <Empty>
              <div className="text-sm text-muted-foreground">
                No memory activity recorded in this window. Counts start from the deploy that added tracking, so an
                empty chart can mean “not measured yet” rather than “never happened”.
              </div>
            </Empty>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={DAMSON_100} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="stage" width={120} />
                  <RechartsTooltip />
                  <Bar dataKey="people" fill={DAMSON_400} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Conversations where memory was used" value={b.memory_used_conversations} subtext="in this window" />
        <Stat label="Edited their memory" value={b.profile_edited} subtext="manual profile edits" />
        <Stat label="Deleted everything" value={b.all_deleted} subtext="used the delete action" />
      </div>
    </>
  );
}
